import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined'
import MenuIcon from '@mui/icons-material/Menu'
import { AppBar, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, IconButton, TextField, Toolbar, Typography } from '@mui/material'
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { DialogClose } from '@/components/DialogClose/DialogClose'
import { EnvironmentLabel } from '@/components/EnvironmentLabel/EnvironmentLabel'
import { appVersion } from '@/config/appVersion'
import { appConfig } from '@/config/environment'
import { NetworkStatus } from '@/components/NetworkStatus/NetworkStatus'
import { useAuth } from '@/features/auth/AuthProvider'
import { useProfile } from '@/features/profile/ProfileProvider'
import { type AppPermission, canOpen, homePath } from '@/features/profile/permissions'
import { NoAccess } from '@/features/profile/components/NoAccess/NoAccess'
import { NotificationSetupPrompt } from '@/features/notifications/components/NotificationSetupPrompt/NotificationSetupPrompt'
import { currentNotificationState, disableCurrentDeviceNotifications, needsHomeScreenForNotifications, enableCurrentDeviceNotifications, markNotificationPromptSeen, notificationSetupState, shouldOfferNotificationPrompt, type NotificationSetupState } from '@/features/notifications/browserPush'
import { waitingSummary } from '@/features/registrations/waitingSummary'
import { useSync } from '@/features/registrations/SyncProvider'
import { useEffect, useRef, useState } from 'react'
import { AccountActionsContext, type AccountActions } from './AccountActions'
import styles from './AppShell.module.scss'

type PrimaryArea = 'events' | 'sessions' | 'check-in' | 'reports' | 'permissions'

const primaryDestinations: Readonly<Record<PrimaryArea, string>> = {
  events: '/events',
  sessions: '/sessions',
  'check-in': '/check-in',
  reports: '/reports',
  permissions: '/team',
}

const primaryNavigationStorageKey = 'outreach-os.primary-navigation'

/** Settings and Help, opened from it, fill the screen. */
export function isFullScreenPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/'
  return path === '/settings' || path === '/help' || path === '/release-notes'
}

function areaForPath(pathname: string): PrimaryArea | null {
  if (pathname === '/events' || pathname.startsWith('/events/')) return 'events'
  // My contacts belongs to the Outreach area.
  if (pathname === '/sessions' || pathname.startsWith('/sessions/') || pathname === '/contacts' || pathname.startsWith('/contacts/')) return 'sessions'
  if (pathname === '/check-in') return 'check-in'
  if (pathname === '/reports' || pathname.startsWith('/reports/')) return 'reports'
  if (pathname === '/team') return 'permissions'
  return null
}

function isDestinationFor(area: PrimaryArea, destination: string): boolean {
  const pathname = destination.split(/[?#]/, 1)[0] ?? ''
  return areaForPath(pathname) === area
}

function readPrimaryDestinations(): Record<PrimaryArea, string> {
  const destinations = { ...primaryDestinations }
  try {
    const saved: unknown = JSON.parse(window.sessionStorage.getItem(primaryNavigationStorageKey) ?? '{}')
    if (!saved || typeof saved !== 'object') return destinations
    for (const area of Object.keys(primaryDestinations) as PrimaryArea[]) {
      const candidate = (saved as Record<string, unknown>)[area]
      if (typeof candidate === 'string' && isDestinationFor(area, candidate)) destinations[area] = candidate
    }
  } catch {
    // Storage can be blocked by a browser privacy setting. The default tabs
    // still work; they simply cannot restore a previous destination.
  }
  return destinations
}

export function AppShell() {
  const auth = useAuth()
  const profile = useProfile()
  const navigate = useNavigate()
  const [showingAbout, setShowingAbout] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const sync = useSync()
  const [confirmingSignOut, setConfirmingSignOut] = useState(false)
  const [notificationPromptOpen, setNotificationPromptOpen] = useState(false)
  const [notificationSettingsOpen, setNotificationSettingsOpen] = useState(false)
  const [notificationState, setNotificationState] = useState<NotificationSetupState>(() => notificationSetupState())
  const [notificationSaving, setNotificationSaving] = useState(false)
  const [notificationError, setNotificationError] = useState<string | null>(null)
  const unsyncedRegistrations = sync.waiting + sync.needsAttention
  const unsyncedCheckIns = sync.checkInsWaiting + sync.checkInsNeedingAttention
  const unsynced = unsyncedRegistrations + unsyncedCheckIns
  const signOut = () => { setConfirmingSignOut(false); void auth.signOut().finally(() => navigate({ to: '/login' })) }
  const [name, setName] = useState(profile.profile?.displayName ?? '')
  const [nameError, setNameError] = useState<string | null>(null)
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  useEffect(() => {
    if (!auth.session || !profile.profile?.onboardingCompletedAt || !shouldOfferNotificationPrompt()) return
    // This is a one-time invitation per device. People can always revisit it
    // from Account → Notifications without being interrupted on every sign-in.
    markNotificationPromptSeen()
    const prompt = window.setTimeout(() => setNotificationPromptOpen(true), 0)
    return () => window.clearTimeout(prompt)
  }, [auth.session, profile.profile?.onboardingCompletedAt])
  // From the browser's permission at first, then whether this device is really subscribed.
  const refreshNotificationState = () => { void currentNotificationState().then(setNotificationState) }
  useEffect(() => { void currentNotificationState().then(setNotificationState) }, [])
  const enableNotifications = async () => {
    setNotificationSaving(true); setNotificationError(null)
    try { await enableCurrentDeviceNotifications(); refreshNotificationState() }
    catch (cause) { setNotificationError(cause instanceof Error ? cause.message : 'Push notifications could not be enabled.') }
    finally { setNotificationSaving(false) }
  }
  const disableNotifications = async () => {
    setNotificationSaving(true); setNotificationError(null)
    try { await disableCurrentDeviceNotifications(); setNotificationState('default') }
    catch (cause) { setNotificationError(cause instanceof Error ? cause.message : 'Push notifications could not be disabled.') }
    finally { setNotificationSaving(false) }
  }
  // Until permissions are known (a first load with nothing cached, or a
  // profile cached before permissions existed) they are unknown: nothing is hidden or blocked yet, rather than flashing a
  // "you don't have access" screen at someone who has it. The database
  // refuses anything they can't do in the meantime.
  const permissions = profile.profile?.permissions ?? undefined
  const allows = (permission: AppPermission) => !permissions || permissions.includes(permission)
  const locationHref = useRouterState({ select: (state) => state.location.href })
  const activeArea = areaForPath(pathname)
  const navigationDestinations = activeArea && isDestinationFor(activeArea, locationHref)
    ? { ...readPrimaryDestinations(), [activeArea]: locationHref }
    : readPrimaryDestinations()
  const eventsActive = pathname === '/events' || pathname.startsWith('/events/')
  // Registering in a session is still the Outreach tab.
  const sessionsActive = areaForPath(pathname) === 'sessions'
  const checkInActive = pathname === '/check-in'
  const reportsActive = pathname === '/reports' || pathname.startsWith('/reports/')
  const permissionsActive = activeArea === 'permissions'

  // Each bottom-bar area is a small workspace in its own right. Remembering
  // its exact address (including a selected event and query-backed sub-tab)
  // lets someone glance at another area and return to their place without
  // creating a second navigation model beside the router.
  useEffect(() => {
    if (!activeArea || !isDestinationFor(activeArea, locationHref)) return
    try {
      const saved = readPrimaryDestinations()
      if (saved[activeArea] !== locationHref) {
        window.sessionStorage.setItem(primaryNavigationStorageKey, JSON.stringify({ ...saved, [activeArea]: locationHref }))
      }
    } catch { /* Storage is optional; the current tab still works without it. */ }
  }, [activeArea, locationHref])

  // Any screen can be a long scroll, so a bar that stays put costs a sixth of
  // a phone screen throughout. Scrolling down tucks it away and hands its
  // height back to the page; the first scroll up brings it back, so the
  // Settings button is never more than a flick away.
  //
  // The state is keyed by path as well as by direction, because a page too
  // short to scroll never fires a scroll event: a bar left tucked on arrival
  // would have no gesture that could return it. Deriving what is visible from
  // the current path means every navigation starts with the bar in place.
  const [tuckedAt, setTuckedAt] = useState<{ path: string; tucked: boolean }>({ path: '', tucked: false })
  // Bars docked above the bottom navigation (the event Details and Form
  // action bars) sit on this measured height, so they meet the nav exactly
  // whatever height the toolbar actually renders at.
  const navigationToolbar = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const nav = navigationToolbar.current?.closest('nav')
    if (!nav) return
    const root = document.documentElement
    const publish = () => root.style.setProperty('--app-bottom-nav-height', `${nav.getBoundingClientRect().height}px`)
    publish()
    if (typeof ResizeObserver === 'undefined') return () => root.style.removeProperty('--app-bottom-nav-height')
    const observer = new ResizeObserver(publish)
    observer.observe(nav)
    return () => {
      observer.disconnect()
      root.style.removeProperty('--app-bottom-nav-height')
    }
  }, [])

  const lastScrollY = useRef(0)
  useEffect(() => {
    lastScrollY.current = window.scrollY
    const onScroll = () => {
      const path = window.location.pathname
      const y = window.scrollY
      const delta = y - lastScrollY.current
      lastScrollY.current = y
      let next: boolean | null = null
      if (y < 96) {
        // Near the top of a page the bar belongs on screen.
        next = false
      } else if (delta >= 6) {
        // A flick down is unambiguous.
        next = true
      } else if (delta <= -24) {
        // A flick up ends with the browser settling back from its own
        // overshoot, which reports as a short upward movement, so revealing
        // has to clear a much larger bar than tucking does. Anything between
        // the two is touch noise.
        next = false
      }
      if (next === null) return
      setTuckedAt((current) => (current.path === path && current.tucked === next ? current : { path, tucked: next }))
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  const tucked = tuckedAt.path === pathname && tuckedAt.tucked
  // Settings and the pages it opens fill the screen, like a phone's settings:
  // no app bar or bottom tabs, just their own Back. The bars are hidden
  // rather than removed, so the bottom bar's measured height stays current.
  const fullScreen = isFullScreenPath(pathname)

  const accountActions: AccountActions = {
    changeName: () => { setName(profile.profile?.displayName ?? ''); setNameError(null); setEditingName(true) },
    pushNotifications: () => { refreshNotificationState(); setNotificationError(null); setNotificationSettingsOpen(true) },
    about: () => setShowingAbout(true),
    // Unsynced registrations stay on the device across a sign-out; say so before it happens.
    signOut: () => { if (unsynced > 0) setConfirmingSignOut(true); else signOut() },
    notificationState,
  }

  return (
    <AccountActionsContext.Provider value={accountActions}>
    <div className={`${styles.shell} ${tucked ? styles.tucked : ''} ${fullScreen ? styles.fullScreen : ''}`}>
      <AppBar className={styles.appBar} color="transparent" component="header" elevation={0} position="fixed">
        <Toolbar className={styles.topToolbar}>
          <div className={styles.brand} aria-label="OutreachOS">
            <span className={styles.brandMark} aria-hidden="true">✶</span>
            <Typography className={styles.wordmark} component="span">OutreachOS</Typography>
            <EnvironmentLabel />
          </div>
          <div className={styles.topActions}><span className={styles.networkStatus}><NetworkStatus /></span><IconButton aria-label="Settings" color="inherit" onClick={() => { if (pathname !== '/settings') void navigate({ to: '/settings', state: (current) => ({ ...current, openedFrom: locationHref }) }) }}><MenuIcon /></IconButton></div>
        </Toolbar>
      </AppBar>
      <Dialog open={confirmingSignOut} onClose={() => setConfirmingSignOut(false)} fullWidth maxWidth="xs">
        <DialogTitle>Sign out before everything has synced?<DialogClose onClose={() => setConfirmingSignOut(false)} /></DialogTitle>
        <DialogContent>
          <Typography>
            {waitingSummary(unsyncedRegistrations, unsyncedCheckIns)} {unsynced === 1 ? 'hasn’t' : 'haven’t'} reached the server yet. {unsynced === 1 ? 'It stays' : 'They stay'} safely on this device and will sync the next time you sign in here.
          </Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setConfirmingSignOut(false)}>Stay signed in</Button><Button color="error" variant="outlined" onClick={signOut}>Sign out</Button></DialogActions>
      </Dialog>
      <Dialog open={showingAbout} onClose={() => setShowingAbout(false)} fullWidth maxWidth="xs" slotProps={{ paper: { 'aria-labelledby': 'about-title' } }}>
        <DialogTitle id="about-title">About OutreachOS<DialogClose onClose={() => setShowingAbout(false)} /></DialogTitle>
        <DialogContent>
          <Typography className={styles.aboutVersion}>Version {appVersion}</Typography>
          <Typography color="text.secondary">{appConfig.environmentLabel === 'LOCAL' ? 'Local' : appConfig.environmentLabel === 'PRODUCTION' ? 'Production' : 'Development'} environment</Typography>
        </DialogContent>
      </Dialog>
      <Dialog open={editingName} onClose={() => setEditingName(false)} fullWidth maxWidth="xs" slotProps={{ paper: { className: styles.accountForm } }}><DialogTitle>Change name<DialogClose onClose={() => setEditingName(false)} /></DialogTitle><form onSubmit={(event) => { event.preventDefault(); setNameError(null); void profile.updateDisplayName(name).then(() => setEditingName(false)).catch((error: unknown) => setNameError(error instanceof Error ? error.message : 'Your name could not be saved.')) }}><DialogContent><TextField autoFocus fullWidth error={Boolean(nameError)} helperText={nameError} label="Your name" value={name} onChange={(event) => setName(event.target.value)} /></DialogContent><DialogActions><Button onClick={() => setEditingName(false)}>Cancel</Button><Button variant="contained" type="submit" disabled={!name.trim()}>Save</Button></DialogActions></form></Dialog>
      <Dialog open={notificationSettingsOpen} onClose={() => !notificationSaving && setNotificationSettingsOpen(false)} fullWidth maxWidth="xs"><DialogTitle>Push notifications<DialogClose onClose={() => !notificationSaving && setNotificationSettingsOpen(false)} /></DialogTitle><DialogContent><Typography>{notificationState === 'enabled' ? 'Push notifications are on: Session broadcasts arrive on this device’s lock screen.' : notificationState === 'blocked' ? 'Push notifications are blocked in this browser. Open this site’s browser settings to allow them, then try again.' : notificationState === 'unsupported' ? (needsHomeScreenForNotifications() ? 'On iPhone and iPad, push notifications only work once OutreachOS is added to your Home Screen. You’ll still see every update in the session’s Updates tab.' : 'This browser does not support push notifications. You’ll still see every update in the session’s Updates tab.') : notificationState === 'unconfigured' ? 'Push notifications are not configured for this environment yet.' : 'Turn on push notifications to get Session broadcasts on this device’s lock screen, even when the app is closed.'}</Typography>{notificationError ? <Typography className={styles.notificationError} color="error">{notificationError}</Typography> : null}</DialogContent><DialogActions>{notificationState === 'enabled' ? <Button color="error" variant="outlined" disabled={notificationSaving} onClick={() => void disableNotifications()}>{notificationSaving ? 'Removing…' : 'Disable push notifications'}</Button> : notificationState === 'unsupported' ? (needsHomeScreenForNotifications() ? <Button variant="contained" onClick={() => { setNotificationSettingsOpen(false); void navigate({ to: '/help', search: { topic: 'install' }, state: (current) => ({ ...current, openedFrom: locationHref }) }) }}>How to add it</Button> : null) : notificationState === 'unconfigured' ? null : <Button variant="contained" disabled={notificationSaving} onClick={() => void enableNotifications()}>{notificationSaving ? 'Enabling…' : 'Enable push notifications'}</Button>}</DialogActions></Dialog>
      <NotificationSetupPrompt open={notificationPromptOpen} onClose={() => { setNotificationPromptOpen(false); refreshNotificationState() }} />
      <Container className={styles.content} component="main" maxWidth="lg">
        {/* The database refuses what someone can't do; this only spares
            them a screen that would fail on them. */}
        {!permissions || canOpen(permissions, pathname) ? <Outlet /> : <NoAccess home={homePath(permissions)} />}
      </Container>
      <Drawer
        anchor="bottom"
        className={styles.navigationDrawer}
        open
        slotProps={{ paper: { component: 'nav', 'aria-label': 'Primary navigation' } }}
        variant="permanent"
      >
        <Toolbar ref={navigationToolbar} className={styles.drawerToolbar}>
          {allows('events') ? (
            <IconButton aria-current={eventsActive ? 'page' : undefined} aria-label="Events" className={eventsActive ? styles.activeNavigation : undefined} color="inherit" component={Link} to={navigationDestinations.events}>
              <CalendarMonthOutlinedIcon />
              <span className={styles.navigationLabel}>Events</span>
            </IconButton>
          ) : null}
          {allows('outreach') ? (
            <IconButton aria-current={sessionsActive ? 'page' : undefined} aria-label="Outreach" className={sessionsActive ? styles.activeNavigation : undefined} color="inherit" component={Link} to={navigationDestinations.sessions}>
              <GroupsOutlinedIcon />
              <span className={styles.navigationLabel}>Outreach</span>
            </IconButton>
          ) : null}
          {allows('check_in') ? (
            <IconButton aria-current={checkInActive ? 'page' : undefined} aria-label="Check-in" className={checkInActive ? styles.activeNavigation : undefined} color="inherit" component={Link} to={navigationDestinations['check-in']}>
              <CheckBoxOutlinedIcon />
              <span className={styles.navigationLabel}>Check-in</span>
            </IconButton>
          ) : null}
          {allows('reports') ? (
            <IconButton aria-current={reportsActive ? 'page' : undefined} aria-label="Reports" className={reportsActive ? styles.activeNavigation : undefined} color="inherit" component={Link} to={navigationDestinations.reports}>
              <AssessmentOutlinedIcon />
              <span className={styles.navigationLabel}>Reports</span>
            </IconButton>
          ) : null}
          {/* Only once permissions are known to include it: the other tabs
              show while permissions load, but nearly no one has this one. */}
          {permissions?.includes('team') ? (
            <IconButton aria-current={permissionsActive ? 'page' : undefined} aria-label="Permissions" className={permissionsActive ? styles.activeNavigation : undefined} color="inherit" component={Link} to="/team">
              <ManageAccountsOutlinedIcon />
              <span className={styles.navigationLabel}>Permissions</span>
            </IconButton>
          ) : null}
        </Toolbar>
      </Drawer>
    </div>
    </AccountActionsContext.Provider>
  )
}
